import JsSIP from 'jssip';
import { useSyncExternalStore, useState, useEffect } from 'react';
import { getSipWssUrl, getSipDomain } from 'src/config/hostname';

// --- Module-level state (singleton) ---

let ua = null;
let session = null;
let localStream = null;
let durationInterval = null;
let audioEl = null;
let isCleaningUp = false;

const IDLE_STATE = {
  status: 'idle',
  isMuted: false,
  duration: 0,
  startTime: null,
  sipUri: null,
  assistanceName: '',
  assistanceType: '',
  aicallId: null,
  error: null,
};

let state = { ...IDLE_STATE };
let listeners = new Set();

// --- Pub/sub for React useSyncExternalStore ---

function notify() {
  state = { ...state };
  listeners.forEach((fn) => fn());
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot() {
  return state;
}

// --- React hook ---

export function useTestCallState() {
  return useSyncExternalStore(subscribe, getSnapshot);
}

// --- Stream access for voice activity analysis ---

export function getLocalStream() {
  return localStream;
}

/**
 * React hook that analyzes the local microphone stream and returns
 * 5 normalized bar heights (0-1) representing voice activity.
 * Uses Web Audio API AnalyserNode on voice-focused frequency bins (85Hz-3kHz).
 */
export function useVoiceActivity(isMuted, isConnected) {
  const [bars, setBars] = useState([0, 0, 0, 0, 0]);

  useEffect(() => {
    const stream = getLocalStream();
    if (!stream || !isConnected || isMuted) {
      setBars([0, 0, 0, 0, 0]);
      return;
    }

    let audioCtx;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    } catch {
      return;
    }

    // Resume AudioContext if suspended (browsers require user gesture)
    if (audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {});
    }

    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.4;
    source.connect(analyser);

    const bufferLength = analyser.frequencyBinCount; // 128
    const dataArray = new Uint8Array(bufferLength);

    // Map frequency bins to voice range (85Hz-3000Hz).
    // Bin resolution = sampleRate / fftSize.
    // Skip bin 0 (DC offset noise).
    const sampleRate = audioCtx.sampleRate;
    const binWidth = sampleRate / analyser.fftSize;
    const startBin = Math.max(1, Math.floor(85 / binWidth));
    const endBin = Math.min(bufferLength - 1, Math.ceil(3000 / binWidth));
    const voiceBins = endBin - startBin + 1;
    const binsPerBar = Math.max(1, Math.floor(voiceBins / 5));

    let rafId;

    const update = () => {
      analyser.getByteFrequencyData(dataArray);

      const newBars = [];
      for (let i = 0; i < 5; i++) {
        const from = startBin + i * binsPerBar;
        const to = Math.min(from + binsPerBar, endBin + 1);
        let sum = 0;
        for (let j = from; j < to; j++) {
          sum += dataArray[j];
        }
        newBars.push(sum / ((to - from) * 255));
      }

      setBars(newBars);
      rafId = requestAnimationFrame(update);
    };

    rafId = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(rafId);
      source.disconnect();
      analyser.disconnect();
      audioCtx.close().catch(() => {});
    };
  }, [isMuted, isConnected]);

  return bars;
}

// --- Shared utility ---

export function formatDuration(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

// --- Audio ---

function getAudioEl() {
  if (!audioEl) {
    audioEl = document.createElement('audio');
    audioEl.autoplay = true;
    audioEl.volume = 1.0;
    // Some browsers require the element to be in the DOM to play
    audioEl.style.display = 'none';
    document.body.appendChild(audioEl);
  }
  return audioEl;
}

// --- Duration timer ---

function startDurationTimer() {
  stopDurationTimer();
  state.startTime = Date.now();
  state.duration = 0;
  durationInterval = setInterval(() => {
    state.duration = Math.floor((Date.now() - state.startTime) / 1000);
    notify();
  }, 1000);
}

function stopDurationTimer() {
  if (durationInterval) {
    clearInterval(durationInterval);
    durationInterval = null;
  }
}

// --- Cleanup ---

function releaseResources() {
  stopDurationTimer();

  if (localStream) {
    localStream.getTracks().forEach((t) => t.stop());
    localStream = null;
  }

  const audio = getAudioEl();
  audio.srcObject = null;
}

function cleanup() {
  if (isCleaningUp) return;
  isCleaningUp = true;

  releaseResources();

  if (session) {
    try { session.terminate(); } catch (e) { /* ignore */ }
    session = null;
  }

  if (ua) {
    try { ua.stop(); } catch (e) { /* ignore */ }
    ua = null;
  }

  isCleaningUp = false;
}

// --- Browser tab close handler ---

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    cleanup();
  });
}

// --- Public API ---

export async function startCall({ sipUri, assistanceName, assistanceType }) {
  if (state.status !== 'idle' && state.status !== 'failed') return;

  state = {
    ...IDLE_STATE,
    status: 'requesting-mic',
    sipUri,
    assistanceName,
    assistanceType,
  };
  notify();

  // 1. Request microphone
  try {
    localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
  } catch (e) {
    state = {
      ...state,
      status: 'failed',
      error: 'Microphone access denied. Please grant permission in your browser settings.',
    };
    notify();
    return;
  }

  // 2. Create JsSIP UA
  state = { ...state, status: 'connecting' };
  notify();

  try {
    const socket = new JsSIP.WebSocketInterface(getSipWssUrl());
    ua = new JsSIP.UA({
      uri: `sip:test@${getSipDomain()}`,
      sockets: [socket],
      register: false,
      session_timers: false,
    });

    ua.on('connected', () => {
      placeCall(sipUri);
    });

    ua.on('disconnected', () => {
      if (state.status !== 'idle' && !isCleaningUp) {
        releaseResources();
        session = null;
        ua = null;
        state = { ...state, status: 'failed', error: 'WebSocket disconnected' };
        notify();
      }
    });

    ua.start();
  } catch (e) {
    state = { ...state, status: 'failed', error: 'Failed to connect: ' + e.message };
    cleanup();
    notify();
  }
}

/**
 * Filter SDP to keep only Opus codec (and telephone-event for DTMF).
 * Removes G722, PCMU, PCMA, CN, RED, and any other non-Opus codecs.
 */
function filterSdpOpusOnly(sdp) {
  const lines = sdp.split('\r\n');
  const result = [];
  // Collect payload types to keep: opus and telephone-event
  const keepPts = new Set();

  // First pass: find payload types for opus and telephone-event
  for (const line of lines) {
    const rtpmapMatch = line.match(/^a=rtpmap:(\d+)\s+(\S+)/);
    if (rtpmapMatch) {
      const pt = rtpmapMatch[1];
      const codec = rtpmapMatch[2].toLowerCase();
      if (codec.startsWith('opus/') || codec.startsWith('telephone-event/')) {
        keepPts.add(pt);
      }
    }
  }

  // Second pass: filter lines
  for (const line of lines) {
    // Rewrite m= line to only include kept payload types
    if (line.startsWith('m=audio')) {
      const parts = line.split(' ');
      // m=audio PORT PROTO PT1 PT2 PT3 ...
      const filtered = parts.slice(0, 3).concat(
        parts.slice(3).filter((pt) => keepPts.has(pt))
      );
      result.push(filtered.join(' '));
      continue;
    }

    // Remove rtpmap/fmtp/rtcp-fb for non-kept codecs
    const attrMatch = line.match(/^a=(rtpmap|fmtp|rtcp-fb):(\d+)/);
    if (attrMatch) {
      if (!keepPts.has(attrMatch[2])) continue; // skip
    }

    result.push(line);
  }

  return result.join('\r\n');
}

function placeCall(sipUri) {
  if (!ua) return;

  try {
    session = ua.call(sipUri, {
      mediaStream: localStream,
      pcConfig: {
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      },
      rtcOfferConstraints: {
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      },
      eventHandlers: {
        sdp: (e) => {
          if (e.originator === 'local') {
            e.sdp = filterSdpOpusOnly(e.sdp);
          }
        },
        peerconnection: (e) => {
          const pc = e.peerconnection;

          // Remote track — attach to audio element for playback
          pc.addEventListener('track', (event) => {
            if (event.streams && event.streams[0]) {
              const audio = getAudioEl();
              audio.srcObject = event.streams[0];
              audio.play().catch((err) => {
                console.warn('[testCall] Audio play() failed:', err.name, err.message);
              });
            }
          });
        },
        progress: () => {
          state = { ...state, status: 'ringing' };
          notify();
        },
        confirmed: () => {
          state = { ...state, status: 'connected' };
          startDurationTimer();
          notify();
        },
        ended: () => {
          cleanup();
          state = { ...IDLE_STATE };
          notify();
        },
        failed: (e) => {
          const cause = e?.cause || 'Call failed';
          cleanup();
          state = { ...IDLE_STATE, status: 'failed', error: cause };
          notify();
        },
      },
    });
  } catch (e) {
    state = { ...state, status: 'failed', error: 'Failed to place call: ' + e.message };
    cleanup();
    notify();
  }
}

export function hangup() {
  cleanup();
  state = { ...IDLE_STATE };
  notify();
}

/**
 * Called by the websocket handler when an aimessage arrives during an active test call.
 * Captures the aicall_id so the Call tab can display messages.
 *
 * Note: Only captures during ringing/connected to avoid associating unrelated
 * aicall_ids (e.g., from a concurrent chat session on the same account).
 */
export function captureAicallId(aicallId) {
  if (!aicallId) return;
  if (state.aicallId) return; // already captured
  if (state.status !== 'ringing' && state.status !== 'connected') return;
  state = { ...state, aicallId };
  notify();
}

export function toggleMute() {
  if (!session || state.status !== 'connected') return;

  try {
    if (state.isMuted) {
      session.unmute({ audio: true });
    } else {
      session.mute({ audio: true });
    }
    state = { ...state, isMuted: !state.isMuted };
    notify();
  } catch (e) {
    console.error('Failed to toggle mute:', e);
  }
}
